import { z } from 'zod';
/**
 * What a chosen password must satisfy (decision D3).
 *
 * Lives in `packages/shared` because both sides must agree. The server enforces it — that
 * is the control, and there is a test that drives the route with the client validator
 * bypassed — while the client uses the same function to tell somebody *before* they submit
 * which rule they have not met yet. When those were separate copies elsewhere in this
 * codebase they drifted, which is the whole reason the contract lives here.
 *
 * Returns codes, not sentences. CLAUDE.md forbids hardcoded user-facing copy, so the
 * client maps each code to an i18n key and the server maps them to one refusal.
 */
/** The floor, not a recommendation. Eight characters with three character classes. */
export const MIN_PASSWORD_LENGTH = 8;
/**
 * A ceiling, because bcrypt is deliberately expensive and an unbounded body is a way to
 * make somebody else's CPU pay for it. Not a strength rule.
 */
export const MAX_PASSWORD_LENGTH = 200;
/**
 * Substrings shorter than this are not worth testing for containment. "Al" appearing in
 * a password says nothing; refusing every password containing it would be absurd.
 */
const MIN_IDENTIFIER_FRAGMENT = 3;
/**
 * Case- and accent-insensitive, so "Chrispin" and "chríspin" are the same fragment.
 *
 * `\p{Diacritic}` rather than a literal combining range, which would put invisible
 * characters in this file. Matches the shape of `fold` in the client's place search,
 * minus its punctuation strip: a password's punctuation is part of it, and removing
 * separators here would make "chris.pin" contain "chrispin" and refuse a password that
 * does not actually carry the name.
 */
function fold(text) {
    return text
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();
}
/**
 * The parts of somebody's identity a password must not contain.
 *
 * The email *local-part* only — the domain is shared by the whole property, so refusing
 * every password containing "nbhops" would reject reasonable passwords for a reason the
 * user cannot see. Names are split on whitespace so a surname is caught on its own.
 */
function identityFragments(context) {
    const localPart = context.email?.split('@')[0] ?? '';
    return [localPart, ...(context.name ?? '').split(/\s+/)]
        .map(fold)
        .filter((fragment) => fragment.length >= MIN_IDENTIFIER_FRAGMENT);
}
/**
 * Every rule this password fails, in the order they are worth fixing.
 *
 * All of them, not the first: somebody typing a new password on a phone should be told
 * everything that is wrong at once rather than discovering the rules one submission at a
 * time.
 */
export function passwordViolations(plaintext, context = {}) {
    const violations = [];
    if (plaintext.length < MIN_PASSWORD_LENGTH)
        violations.push('tooShort');
    if (plaintext.length > MAX_PASSWORD_LENGTH)
        violations.push('tooLong');
    if (!/[A-Z]/.test(plaintext))
        violations.push('needsUppercase');
    if (!/[a-z]/.test(plaintext))
        violations.push('needsLowercase');
    if (!/[0-9]/.test(plaintext))
        violations.push('needsNumber');
    // Compared as typed. A password differing from the old one only by case is a different
    // password, and treating it as the same would refuse a legitimate change.
    if (context.currentPassword && plaintext === context.currentPassword) {
        violations.push('sameAsCurrent');
    }
    // The rule that catches what a composition check waves through. "Chrispin2026" has an
    // uppercase, a lowercase, a number and nine characters, and is the first thing an
    // attacker who knows the staff list would try.
    const folded = fold(plaintext);
    if (identityFragments(context).some((fragment) => folded.includes(fragment))) {
        violations.push('containsIdentity');
    }
    return violations;
}
/** Convenience for the call sites that only need a yes or no. */
export function isPasswordAcceptable(plaintext, context = {}) {
    return passwordViolations(plaintext, context).length === 0;
}
/**
 * The self-service change body (decision D4).
 *
 * `currentPassword` is required: this route is reachable by any authenticated session, so
 * without it a borrowed handset is a password change. It is bounded but not policy-checked
 * — it is being verified against a stored hash, not chosen, and an old password that
 * predates the policy must still be typeable.
 *
 * `confirmPassword` is right here and wrong for the admin flow. A user is typing something
 * only they will ever know, so a typo they cannot see is the failure mode. An admin
 * reading a temporary password down a corridor needs to *see* it, which is a show/hide
 * toggle and no confirmation at all.
 *
 * The policy itself is not enforced in this schema. It needs the actor's name and email to
 * apply the identity rules, and a Zod schema for a request body does not have them — the
 * route applies it once the session is resolved.
 */
export const changePasswordBodySchema = z
    .object({
    currentPassword: z.string().min(1).max(MAX_PASSWORD_LENGTH),
    newPassword: z.string().min(1).max(MAX_PASSWORD_LENGTH),
    confirmPassword: z.string().min(1).max(MAX_PASSWORD_LENGTH),
})
    .refine((body) => body.newPassword === body.confirmPassword, {
    // Reported against the field the user has to retype, not against the form.
    path: ['confirmPassword'],
    message: 'The two passwords do not match',
});
//# sourceMappingURL=password-policy.js.map